import { useId } from 'react';
import type { CSSProperties, JSX, ReactNode } from 'react';
import { Icon } from './Icon';

export function Field({
  label,
  hint,
  children,
  className,
  style,
}: {
  readonly label?: string | undefined;
  readonly hint?: ReactNode | undefined;
  readonly children: ReactNode;
  readonly className?: string | undefined;
  readonly style?: CSSProperties | undefined;
}): JSX.Element {
  return (
    <div className={`field ${className ?? ''}`} style={style}>
      {label !== undefined && <label className="lb">{label}</label>}
      {children}
      {hint !== undefined && <p className="hint">{hint}</p>}
    </div>
  );
}

export function TextInput({
  value,
  onChange,
  placeholder,
  type = 'text',
  disabled = false,
  ariaLabel,
  style,
}: {
  readonly value: string;
  readonly onChange: (value: string) => void;
  readonly placeholder?: string | undefined;
  readonly type?: 'text' | 'number' | undefined;
  readonly disabled?: boolean | undefined;
  readonly ariaLabel?: string | undefined;
  readonly style?: CSSProperties | undefined;
}): JSX.Element {
  return (
    <input
      className="inp"
      type={type}
      value={value}
      placeholder={placeholder}
      disabled={disabled}
      aria-label={ariaLabel}
      style={style}
      onChange={(e) => {
        onChange(e.target.value);
      }}
    />
  );
}

/**
 * Number input that commits on blur/Enter rather than per keystroke.
 *
 * Committing per keystroke makes an empty field parse as 0 and fights the user
 * as they retype, which the prototype's rights-line editor did.
 */
export function NumberInput({
  value,
  onCommit,
  step,
  min,
  max,
  disabled = false,
  ariaLabel,
  style,
}: {
  readonly value: number;
  readonly onCommit: (value: number) => void;
  readonly step?: number | 'any' | undefined;
  readonly min?: number | undefined;
  readonly max?: number | undefined;
  readonly disabled?: boolean | undefined;
  readonly ariaLabel?: string | undefined;
  readonly style?: CSSProperties | undefined;
}): JSX.Element {
  return (
    <input
      className="inp"
      type="number"
      defaultValue={value}
      key={value}
      step={step}
      min={min}
      max={max}
      disabled={disabled}
      aria-label={ariaLabel}
      style={{ textAlign: 'right', ...style }}
      onBlur={(e) => {
        const parsed = Number(e.target.value);
        onCommit(Number.isFinite(parsed) ? parsed : 0);
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter') e.currentTarget.blur();
      }}
    />
  );
}

export interface SelectOption {
  readonly value: string;
  readonly label: string;
  /**
   * Listed but not choosable. Used where an option describes a surface that
   * exists in the product but is not wired yet — showing it greyed is honest,
   * where hiding it makes the user wonder whether it exists at all.
   */
  readonly disabled?: boolean | undefined;
}

export function Select({
  value,
  onChange,
  options,
  placeholder,
  disabled = false,
  ariaLabel,
  style,
}: {
  readonly value: string;
  readonly onChange: (value: string) => void;
  readonly options: readonly SelectOption[];
  /** Rendered as an empty-valued first option. */
  readonly placeholder?: string | undefined;
  readonly disabled?: boolean | undefined;
  readonly ariaLabel?: string | undefined;
  readonly style?: CSSProperties | undefined;
}): JSX.Element {
  return (
    <select
      className="inp"
      value={value}
      disabled={disabled}
      aria-label={ariaLabel}
      style={style}
      onChange={(e) => {
        onChange(e.target.value);
      }}
    >
      {placeholder !== undefined && <option value="">{placeholder}</option>}
      {options.map((o) => (
        <option key={o.value} value={o.value} disabled={o.disabled ?? false}>
          {o.label}
        </option>
      ))}
    </select>
  );
}

/** Builds options from a plain string list, where value === label. */
export const asOptions = (values: readonly string[]): readonly SelectOption[] =>
  values.map((v) => ({ value: v, label: v }));

export function TextArea({
  value,
  onChange,
  placeholder,
  disabled = false,
  readOnly = false,
  ariaLabel,
  style,
}: {
  readonly value: string;
  readonly onChange?: ((value: string) => void) | undefined;
  readonly placeholder?: string | undefined;
  readonly disabled?: boolean | undefined;
  readonly readOnly?: boolean | undefined;
  readonly ariaLabel?: string | undefined;
  readonly style?: CSSProperties | undefined;
}): JSX.Element {
  return (
    <textarea
      className="inp"
      value={value}
      placeholder={placeholder}
      disabled={disabled}
      readOnly={readOnly}
      aria-label={ariaLabel}
      style={style}
      onChange={(e) => {
        onChange?.(e.target.value);
      }}
    />
  );
}

export function Checkbox({
  checked,
  onChange,
  disabled = false,
  children,
  className,
  style,
}: {
  readonly checked: boolean;
  readonly onChange: (checked: boolean) => void;
  readonly disabled?: boolean | undefined;
  readonly children?: ReactNode | undefined;
  readonly className?: string | undefined;
  readonly style?: CSSProperties | undefined;
}): JSX.Element {
  return (
    <label className={`chk ${disabled ? 'dis' : ''} ${className ?? ''}`} style={style}>
      <input
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={(e) => {
          onChange(e.target.checked);
        }}
      />
      {children !== undefined && <span style={{ flex: 1 }}>{children}</span>}
    </label>
  );
}

export function Slider({
  value,
  onChange,
  min = 0,
  max = 100,
  step = 1,
  disabled = false,
  ariaLabel,
}: {
  readonly value: number;
  readonly onChange: (value: number) => void;
  readonly min?: number | undefined;
  readonly max?: number | undefined;
  readonly step?: number | undefined;
  readonly disabled?: boolean | undefined;
  readonly ariaLabel?: string | undefined;
}): JSX.Element {
  return (
    <input
      className="rng"
      type="range"
      min={min}
      max={max}
      step={step}
      value={value}
      disabled={disabled}
      aria-label={ariaLabel}
      onChange={(e) => {
        onChange(Number(e.target.value));
      }}
    />
  );
}

/** Slider with its current value pinned to the right. */
export function SliderRow({
  value,
  display,
  onChange,
  min,
  max,
  step,
  disabled = false,
  ariaLabel,
}: {
  readonly value: number;
  readonly display: ReactNode;
  readonly onChange: (value: number) => void;
  readonly min?: number | undefined;
  readonly max?: number | undefined;
  readonly step?: number | undefined;
  readonly disabled?: boolean | undefined;
  readonly ariaLabel?: string | undefined;
}): JSX.Element {
  return (
    <div className="slider-row">
      <Slider
        value={value}
        onChange={onChange}
        {...(min === undefined ? {} : { min })}
        {...(max === undefined ? {} : { max })}
        {...(step === undefined ? {} : { step })}
        disabled={disabled}
        {...(ariaLabel === undefined ? {} : { ariaLabel })}
      />
      <span className="slider-val">{display}</span>
    </div>
  );
}

export function SearchInput({
  value,
  onChange,
  placeholder = 'Search…',
  style,
}: {
  readonly value: string;
  readonly onChange: (value: string) => void;
  readonly placeholder?: string | undefined;
  readonly style?: CSSProperties | undefined;
}): JSX.Element {
  const id = useId();
  return (
    <div className="search-wrap" style={style}>
      <Icon name="search" />
      <input
        id={id}
        className="inp"
        value={value}
        placeholder={placeholder}
        aria-label={placeholder}
        onChange={(e) => {
          onChange(e.target.value);
        }}
      />
    </div>
  );
}
